import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

// StatTrendChart — agent / admin 两 SPA 共享的「数据统计」多指标趋势折线图。
//
// 顶部一排 metric 切换 chip(5 选 1)+ 7/30/90 天时间窗 toggle;选哪个 metric 画哪条线。
// 视觉对齐现有 agent dashboard 趋势卡(橙色线 + 浅网格)。recharts 依赖下沉 shared,
// 两 SPA 共用一个组件。

/** 趋势数据点(后端 stats.DayPoint JSON 形状)。 */
export type DayPoint = {
  date: string
  visit_ip: number
  login_user: number
  new_user: number
  total_user: number
  commission_coin: number
}

/** 可切换的指标键。 */
export type MetricKey =
  | 'visit_ip'
  | 'login_user'
  | 'new_user'
  | 'total_user'
  | 'commission_coin'

/** chip 顺序(与 KPI 卡片一致)。 */
export const METRIC_ORDER: MetricKey[] = [
  'visit_ip',
  'login_user',
  'new_user',
  'total_user',
  'commission_coin',
]

/** metric → 中文短标签(chip / caption / 排行表头共用)。 */
export const METRIC_LABELS: Record<MetricKey, string> = {
  visit_ip: '访问IP',
  login_user: '登录用户',
  new_user: '新注册',
  total_user: '总存量',
  commission_coin: '新增佣金',
}

export type StatWindow = '7d' | '30d' | '90d'

const DEFAULT_ACCENT = '#F59E0B'

export type StatTrendChartProps = {
  series: DayPoint[]
  metric: MetricKey
  onMetricChange: (m: MetricKey) => void
  window: StatWindow
  onWindowChange: (w: StatWindow) => void
  accent?: string // agent 橙 #F59E0B(默认) / admin 蓝 #3B82F6
}

export function StatTrendChart({
  series,
  metric,
  onMetricChange,
  window,
  onWindowChange,
  accent = DEFAULT_ACCENT,
}: StatTrendChartProps) {
  return (
    <div
      data-testid="stat-trend-chart"
      className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-5"
    >
      {/* 头部:标题 | 时间窗(pen trendHeader/chartHeader) */}
      <header className="flex items-center justify-between">
        <h2 className="text-[15px] font-semibold text-gray-900">{METRIC_LABELS[metric]}趋势</h2>
        <StatWindowSwitch value={window} onChange={onWindowChange} accent={accent} />
      </header>

      {/* metric chips 独立一行(pen metricChips) */}
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="指标切换">
        {METRIC_ORDER.map((key) => {
          const active = key === metric
          return (
            <button
              key={key}
              type="button"
              data-testid={`stat-metric-chip-${key}`}
              aria-pressed={active}
              onClick={() => onMetricChange(key)}
              className="rounded-md px-3 py-1 text-[12px] font-medium transition-colors"
              style={
                active
                  ? { backgroundColor: accent, color: '#FFFFFF' }
                  : { backgroundColor: '#F3F4F6', color: '#6B7280' }
              }
            >
              {METRIC_LABELS[key]}
            </button>
          )
        })}
      </div>

      {/* 折线图 */}
      <div className="h-[260px] w-full">
        {series.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 6, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#9CA3AF' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }}
                cursor={{ stroke: accent, strokeWidth: 1, strokeDasharray: '3 3' }}
                formatter={(value: number) => [value, METRIC_LABELS[metric]]}
              />
              <Line
                type="monotone"
                dataKey={metric}
                name={METRIC_LABELS[metric]}
                stroke={accent}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4, fill: accent, stroke: '#fff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="flex h-full items-center justify-center text-sm text-gray-400">
            暂无趋势数据
          </p>
        )}
      </div>
    </div>
  )
}

function StatWindowSwitch({
  value,
  onChange,
  accent,
}: {
  value: StatWindow
  onChange: (v: StatWindow) => void
  accent: string
}) {
  const options: Array<{ key: StatWindow; label: string }> = [
    { key: '7d', label: '7 天' },
    { key: '30d', label: '30 天' },
    { key: '90d', label: '90 天' },
  ]
  return (
    <div
      data-testid="stat-trend-window"
      className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white p-0.5"
      role="tablist"
    >
      {options.map((o) => {
        const active = o.key === value
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            data-testid={`stat-window-${o.key}`}
            aria-selected={active}
            onClick={() => onChange(o.key)}
            className={[
              'rounded px-3 py-1 text-[12px] font-medium transition-colors',
              active ? 'text-white' : 'text-gray-600 hover:bg-gray-100',
            ].join(' ')}
            style={active ? { backgroundColor: accent } : undefined}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
